import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { PushCoverageCard } from "@/components/avvisi/PushCoverageCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Paperclip, Link as LinkIcon, Eye, Bell, X, Users, Search, Check, Archive, ArchiveRestore, BellRing } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { StudentPickerMulti } from "@/components/StudentPicker";

export default function AvvisiAdmin() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readsOpen, setReadsOpen] = useState(null); // avviso id
  const [tab, setTab] = useState("active"); // active | archived
  const [form, setForm] = useState(getEmpty());

  function getEmpty() {
    return {
      title: "", body: "", priority: "normal", expires_at: "",
      targets: { all_students: false, year_levels: [], track_types: [], student_ids: [] },
      attachments: [],
    };
  }

  const { data: avvisi = [] } = useQuery({
    queryKey: ["avvisi-admin", tab],
    queryFn: async () => (await api.get("/avvisi", { params: { archived: tab === "archived" } })).data,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-list"],
    queryFn: async () => (await api.get("/students")).data,
  });

  const archiveMut = useMutation({
    mutationFn: async ({ id, restore }) => (await api.post(`/avvisi/${id}/${restore ? "unarchive" : "archive"}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["avvisi-admin"] }); toast.success("Avviso aggiornato"); },
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = { ...form };
      if (!body.expires_at) delete body.expires_at;
      if (editing) return (await api.patch(`/avvisi/${editing.id}`, body)).data;
      return (await api.post("/avvisi", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Avviso aggiornato" : "Avviso pubblicato");
      qc.invalidateQueries({ queryKey: ["avvisi-admin"] });
      setOpen(false); setEditing(null); setForm(getEmpty());
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/avvisi/${id}`)).data,
    onSuccess: () => { toast.success("Avviso eliminato"); qc.invalidateQueries({ queryKey: ["avvisi-admin"] }); },
  });

  const uploadMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/avvisi/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (att) => {
      setForm((f) => ({ ...f, attachments: [...f.attachments, att] }));
      toast.success(`File "${att.name}" allegato`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore upload"),
  });

  const openNew = () => { setEditing(null); setForm(getEmpty()); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title, body: a.body, priority: a.priority || "normal",
      expires_at: a.expires_at || "",
      targets: a.targets || getEmpty().targets,
      attachments: a.attachments || [],
    });
    setOpen(true);
  };

  const toggleYear = (y) => {
    const set = new Set(form.targets.year_levels);
    set.has(y) ? set.delete(y) : set.add(y);
    setForm({ ...form, targets: { ...form.targets, year_levels: [...set] } });
  };
  const toggleTrack = (t) => {
    const set = new Set(form.targets.track_types);
    set.has(t) ? set.delete(t) : set.add(t);
    const years = set.has("modulare") ? form.targets.year_levels : form.targets.year_levels.filter((y) => y <= 3);
    setForm({ ...form, targets: { ...form.targets, track_types: [...set], year_levels: years } });
  };
  const addLink = () => {
    const url = prompt("URL del link:");
    if (!url) return;
    const name = prompt("Nome visualizzato:", url) || url;
    setForm({ ...form, attachments: [...form.attachments, { name, url, kind: "link" }] });
  };
  const removeAtt = (idx) => setForm({ ...form, attachments: form.attachments.filter((_, i) => i !== idx) });

  const targetSummary = (a) => {
    const t = a.targets || {};
    if (t.all_students) return "Tutti gli studenti del residenziale";
    const parts = [];
    if (t.year_levels?.length) parts.push(`Anni ${t.year_levels.join(",")}`);
    if (t.track_types?.length) parts.push(t.track_types.join(","));
    if (t.student_ids?.length) parts.push(`${t.student_ids.length} studenti singoli`);
    return parts.join(" · ") || "—";
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Bacheca Avvisi"
        subtitle="Pubblica comunicazioni per singoli studenti o gruppi. Traccia chi ha letto."
        actions={
          <div className="flex gap-2 items-center">
            <div className="inline-flex rounded-full border border-stone-200 p-0.5 bg-white">
              <button onClick={() => setTab("active")} data-testid="tab-avvisi-attivi" className={`px-3 py-1 text-xs rounded-full transition ${tab === "active" ? "bg-[#1C352D] text-white" : "text-stone-600"}`}>Attivi</button>
              <button onClick={() => setTab("archived")} data-testid="tab-avvisi-archivio" className={`px-3 py-1 text-xs rounded-full transition ${tab === "archived" ? "bg-[#1C352D] text-white" : "text-stone-600"}`}>Archivio</button>
            </div>
            {tab === "active" && (
              <Button data-testid="avviso-add-btn" onClick={openNew} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
                <Plus size={14} className="mr-1.5"/> Nuovo avviso
              </Button>
            )}
          </div>
        }
      />

      {tab === "active" && <PushCoverageCard/>}

      <div className="space-y-3">
        {avvisi.length === 0 && <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessun avviso pubblicato.</Card>}
        {avvisi.map((a) => {
          const readRate = a.target_count > 0 ? Math.round((a.read_count / a.target_count) * 100) : 0;
          const isUrgent = a.priority === "urgent";
          return (
            <Card key={a.id} className={`p-4 border rounded-xl shadow-none ${isUrgent ? "border-red-300 bg-red-50/30" : "border-stone-200"}`} data-testid={`avviso-${a.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {isUrgent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white font-medium">URGENTE</span>}
                    <h3 className="font-semibold text-stone-900">{a.title}</h3>
                  </div>
                  <div className="text-[11px] text-stone-500 mt-1">
                    {fmtDate(a.created_at)} · da {a.created_by_name || "—"} · <Users size={11} className="inline mb-0.5"/> {targetSummary(a)}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {a.push_delivered != null && (
                    <span className="text-[11px] text-stone-500 mr-1 inline-flex items-center gap-1" title="Notifiche push consegnate ai dispositivi" data-testid={`avviso-push-${a.id}`}>
                      <BellRing size={11}/> {a.push_delivered}
                    </span>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setReadsOpen(a.id)} data-testid={`avviso-reads-${a.id}`} className="text-xs h-7">
                    <Eye size={11} className="mr-1"/> {a.read_count}/{a.target_count} ({readRate}%)
                  </Button>
                  {tab === "active" ? (
                    <>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(a)} title="Modifica"><Pencil size={12}/></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-700" onClick={() => archiveMut.mutate({ id: a.id, restore: false })} title="Archivia" data-testid={`avviso-archive-${a.id}`}><Archive size={12}/></Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-700" onClick={() => archiveMut.mutate({ id: a.id, restore: true })} title="Ripristina" data-testid={`avviso-unarchive-${a.id}`}><ArchiveRestore size={12}/></Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => { if (confirm(`Eliminare definitivamente "${a.title}"?`)) delMut.mutate(a.id); }} title="Elimina definitivamente" data-testid={`avviso-delete-${a.id}`}><Trash2 size={12}/></Button>
                </div>
              </div>
              <div className="text-sm text-stone-700 whitespace-pre-wrap">{a.body}</div>
              {a.attachments?.length > 0 && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {a.attachments.map((att, i) => (
                    <a key={i} href={att.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-stone-200 text-xs hover:bg-stone-50">
                      {att.kind === "link" ? <LinkIcon size={11}/> : <Paperclip size={11}/>}
                      {att.name}
                    </a>
                  ))}
                </div>
              )}
              {/* Progress bar lettura */}
              <div className="mt-3 h-1 bg-stone-100 rounded-full overflow-hidden">
                <div className={`h-full transition-all ${readRate === 100 ? "bg-emerald-500" : readRate > 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${readRate}%` }}/>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Dialog create/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica avviso" : "Nuovo avviso"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Titolo</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="avviso-title" className="mt-1.5" placeholder="Es. Chiusura scuola 15 novembre"/></div>
              <div>
                <Label>Priorità</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normale</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Testo</Label><Textarea value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="avviso-body" rows={5} className="mt-1.5" placeholder="Contenuto dell'avviso..."/></div>

            <div className="border border-stone-200 rounded-lg p-3 bg-stone-50/50">
              <Label className="mb-2 block">Destinatari</Label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.targets.all_students} onChange={(e) => setForm({...form, targets: {...form.targets, all_students: e.target.checked}})} data-testid="avviso-all"/>
                <span className="font-medium">Tutti gli studenti del residenziale</span>
              </label>
              <p className="text-[11px] text-stone-500 mt-1">Gli studenti del modulare ricevono l'avviso solo se scegli il percorso «modulare» o li selezioni singolarmente. Il filtro per anno senza percorso vale per il residenziale.</p>
              {!form.targets.all_students && (
                <div className="mt-3 space-y-2.5">
                  <div>
                    <div className="text-xs text-stone-600 mb-1">Filtra per anno:</div>
                    <div className="flex gap-1.5">
                      {(form.targets.track_types.includes("modulare") ? [1,2,3,4,5,6,7] : [1,2,3]).map((y) => (
                        <button key={y} type="button" onClick={() => toggleYear(y)} className={`px-3 py-1 rounded-full text-xs border ${form.targets.year_levels.includes(y) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>{y}°</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-600 mb-1">Filtra per percorso:</div>
                    <div className="flex gap-1.5">
                      {["residenziale","modulare"].map((t) => (
                        <button key={t} type="button" onClick={() => toggleTrack(t)} className={`px-3 py-1 rounded-full text-xs border capitalize ${form.targets.track_types.includes(t) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200"}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-600 mb-1">Studenti singoli aggiuntivi:</div>
                    <StudentPickerMulti students={students} values={form.targets.student_ids} onChange={(ids) => setForm({...form, targets: {...form.targets, student_ids: ids}})} testId="avviso-students"/>
                  </div>
                </div>
              )}
            </div>

            <div className="border border-stone-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="!mb-0">Allegati e link</Label>
                <div className="flex gap-1.5">
                  <label className="inline-flex items-center gap-1 text-xs cursor-pointer px-2 py-1 rounded-full border border-stone-200 hover:bg-stone-50">
                    <Paperclip size={11}/> File
                    <input type="file" hidden onChange={(e) => { if (e.target.files?.[0]) uploadMut.mutate(e.target.files[0]); e.target.value=""; }} data-testid="avviso-upload"/>
                  </label>
                  <button type="button" onClick={addLink} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-stone-200 hover:bg-stone-50">
                    <LinkIcon size={11}/> Link
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                {form.attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-stone-50 px-2 py-1 rounded">
                    {a.kind === "link" ? <LinkIcon size={11}/> : <Paperclip size={11}/>}
                    <span className="flex-1 truncate">{a.name}</span>
                    <button type="button" onClick={() => removeAtt(i)} className="text-red-600"><X size={11}/></button>
                  </div>
                ))}
                {form.attachments.length === 0 && <div className="text-[10px] text-stone-400 italic">Nessun allegato</div>}
              </div>
            </div>

            <div><Label>Scade il (opzionale)</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} className="mt-1.5"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="avviso-save" className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!form.title || !form.body || saveMut.isPending} onClick={() => saveMut.mutate()}>
              {saveMut.isPending ? "Pubblicazione…" : (editing ? "Salva" : "Pubblica")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReadsDialog id={readsOpen} onClose={() => setReadsOpen(null)}/>
    </div>
  );
}

function ReadsDialog({ id, onClose }) {
  const { data = [] } = useQuery({
    queryKey: ["avviso-reads", id],
    queryFn: async () => (await api.get(`/avvisi/${id}/reads`)).data,
    enabled: !!id,
  });
  const readCount = data.filter((r) => r.read).length;
  return (
    <Dialog open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Prese visione ({readCount} / {data.length})</DialogTitle></DialogHeader>
        <div className="space-y-1">
          {data.map((r) => (
            <div key={r.student_id} className="flex items-center gap-2 py-1 text-sm border-b border-stone-100">
              <span className={`w-4 h-4 rounded-full flex items-center justify-center ${r.read ? "bg-emerald-500 text-white" : "bg-stone-200"}`}>
                {r.read && <Check size={10}/>}
              </span>
              <span className="flex-1 truncate">{r.name}</span>
              {r.read ? <span className="text-[10px] text-emerald-700">letto {fmtDate(r.read_at)}</span> : <span className="text-[10px] text-stone-400">non letto</span>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
